/**
 * Shared presence helpers — derive online/last-seen state from a
 * `last_seen` ISO timestamp returned by the API. The Worker also bumps
 * the underlying column on every authenticated request (debounced 30s).
 *
 * Threshold matches the backend's /api/profile derivation:
 *   < 90s   → "Online now"
 *   < 5 min → "Last seen Xm ago"
 *   < 1 hr  → "Last seen Xm ago"
 *   < 24 hr → "Last seen Xh ago"
 *   ≥ 24 hr → "Last seen Xd ago"
 *   null    → "Offline"
 */

export const ONLINE_THRESHOLD_MS = 90_000;

export function isOnline(lastSeen?: string | null): boolean {
  if (!lastSeen) return false;
  const ms = Date.parse(lastSeen);
  if (!ms) return false;
  return Date.now() - ms < ONLINE_THRESHOLD_MS;
}

export function lastSeenLabel(lastSeen?: string | null): string {
  if (!lastSeen) return 'Offline';
  const ms = Date.parse(lastSeen);
  if (!ms) return 'Offline';
  const diff = Date.now() - ms;
  if (diff < ONLINE_THRESHOLD_MS) return 'Online now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `Last seen ${min}m ago`;
  const hr = Math.floor(diff / 3_600_000);
  if (hr < 24) return `Last seen ${hr}h ago`;
  const d = Math.floor(diff / 86_400_000);
  if (d < 7) return `Last seen ${d}d ago`;
  // Older than a week — show a date.
  try {
    return `Last seen ${new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
  } catch {
    return 'Offline';
  }
}
